import { useState } from 'react';

import {
  Avatar,
  AvatarGroup,
  Box,
  Divider,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Layout from '@/components/layout/Layout';
import CamShell, { type CamNavItem } from '@/cam-admin/CamShell';
import { CAM_COMPACT } from '@/cam-admin/responsive';
import {
  CARE_CIRCLES,
  SIGNED_IN_CAM,
  type CareCircleRow,
} from '@/cam-admin/care-circles/fixtures';
import {
  ACTIVITY_LOG,
  LAST_ACTIVITY,
} from '@/cam-admin/descoping/DescopingCaseloads.fixtures';
import { CIRCLE_PATH } from '@/cam-admin/descoping/descopingNav';
import SearchField from '@/components/mui/SearchField';

const CIRCLE_COLUMNS = [
  'Care recipient',
  'Primary carer',
  'Email',
  'Care circle ID',
  'Last activity',
  'Watchers',
];

const ACTIVITY_COLUMNS = ['Activity', 'Date', 'Care circle'];

const CIRCLE_BY_ID = new Map(CARE_CIRCLES.map((c) => [c.id, c]));

// Free-text match across a circle's people and reference — the caseloads search.
const matches = (circle: CareCircleRow, query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [circle.carer, circle.careRecipient, circle.email, circle.id].some(
    (field) => field.toLowerCase().includes(q),
  );
};

type CaseloadTab = 'circles' | 'activity';

const TABS: { id: CaseloadTab; label: string }[] = [
  { id: 'circles', label: 'Circles' },
  { id: 'activity', label: 'Activity log' },
];

/**
 * The descoping Caseloads view: the signed-in case manager's own caseload — the
 * care circles assigned to them (Circles tab) and a log of recent activity across
 * them (Activity log tab), each row linking back to its care circle.
 * Presentational — the rail, bell and circle links route through `onNavigate`.
 */
const DescopingCaseloads = ({
  nav,
  onNavigate,
}: {
  nav?: CamNavItem[];
  onNavigate?: (path: string) => void;
}) => {
  const [tab, setTab] = useState<CaseloadTab>('circles');
  const [query, setQuery] = useState('');

  const rows = CARE_CIRCLES.filter(
    (c) => c.caseManager?.name === SIGNED_IN_CAM,
  );
  const shownRows = rows.filter((c) => matches(c, query));
  const caseloadIds = new Set(rows.map((r) => r.id));
  const activities = ACTIVITY_LOG.filter((a) => caseloadIds.has(a.circleId));

  // Opening a care circle — from an activity row — routes to its control panel
  // inside the descoping prototype (not the shared /care-search route).
  const openCircle = () => onNavigate?.(CIRCLE_PATH);

  return (
    <CamShell
      activeNav='Your caseloads'
      nav={nav}
      onNavigate={onNavigate}
      onBell={() => onNavigate?.('/care-search/notifications')}
    >
      <Layout sx={{ [CAM_COMPACT]: { p: 2 } }}>
        <Typography sx={{ mb: 1 }} variant='h3' color='primary.main'>
          Your caseloads
        </Typography>

        {/* MUI's sliding indicator measures in visual pixels, which lands under
            the wrong tab inside the scaled laptop frame — use a CSS underline on
            the selected tab instead (matches the care-circles tabs). */}
        <Tabs
          slotProps={{ indicator: { sx: { display: 'none' } } }}
          value={TABS.findIndex((t) => t.id === tab)}
          onChange={(_, i) => setTab(TABS[i].id)}
          variant='scrollable'
          scrollButtons={false}
        >
          {TABS.map((t) => (
            <Tab
              key={t.id}
              label={t.label}
              sx={{
                '&.Mui-selected': {
                  boxShadow: (theme) =>
                    `inset 0 -2px 0 ${theme.palette.primary.main}`,
                },
              }}
            />
          ))}
        </Tabs>
        <Divider />

        {tab === 'circles' ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              pt: 3,
            }}
          >
            <Box sx={{ width: 320, maxWidth: '100%', mb: 3 }}>
              <SearchField
                placeholder='Search your caseloads'
                value={query}
                onChange={setQuery}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {CIRCLE_COLUMNS.map((col) => (
                      <TableCell key={col}>
                        <Typography variant='body2' color='text.secondary'>
                          {col}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shownRows.map((circle) => (
                    <TableRow key={circle.id}>
                      <TableCell>{circle.careRecipient}</TableCell>
                      <TableCell>{circle.carer}</TableCell>
                      <TableCell>{circle.email}</TableCell>
                      <TableCell>{circle.id}</TableCell>
                      <TableCell>{LAST_ACTIVITY[circle.id] ?? '—'}</TableCell>
                      <TableCell>
                        {circle.watchers.length > 0 ? (
                          <AvatarGroup
                            max={3}
                            sx={{
                              justifyContent: 'flex-end',
                              '& .MuiAvatar-root': {
                                width: 28,
                                height: 28,
                                fontSize: 12,
                              },
                            }}
                          >
                            {circle.watchers.map((w) => (
                              <Avatar
                                key={w}
                                sx={{
                                  bgcolor: 'custom.limeGreen.500',
                                  color: 'primary.main',
                                }}
                              >
                                {w}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                        ) : (
                          <Box
                            sx={{ color: 'text.secondary', display: 'flex' }}
                          >
                            <Icon icon='eye' fontSize={16} />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1, pt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {ACTIVITY_COLUMNS.map((col) => (
                    <TableCell key={col}>
                      <Typography variant='body2' color='text.secondary'>
                        {col}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((a) => {
                  const circle = CIRCLE_BY_ID.get(a.circleId);
                  return (
                    <TableRow key={a.id}>
                      <TableCell>{a.action}</TableCell>
                      <TableCell>{a.date}</TableCell>
                      <TableCell>
                        <Link
                          component='button'
                          variant='body2'
                          color='primary.main'
                          underline='hover'
                          onClick={openCircle}
                        >
                          {circle ? circle.careRecipient : a.circleId}
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Layout>
    </CamShell>
  );
};

export default DescopingCaseloads;
